<template>
  <Teleport to='body'>
    <button
      v-if="text"
      @click="scrollToAnchor"
      class="floating-button"
      :class="{ hidden: isInView }"
    >
      <Arrow class="arrow-icon" />
      <span>{{ text }}</span>
    </button>
  </Teleport>
</template>

<script setup>
const props = defineProps({
  text: String,
  anchorId: String
});

const isInView = ref(false);

const scrollToAnchor = () => {
  if (props.anchorId) {
    document.getElementById(props.anchorId)?.scrollIntoView({ behavior: 'smooth' });
  }
};

onMounted(() => {
  if (!props.anchorId) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      isInView.value = entry.isIntersecting;
    },
    { threshold: 0.1 }
  );

  const element = document.getElementById(props.anchorId);
  if (element) observer.observe(element);

  onUnmounted(() => observer.disconnect());
});
</script>

<style lang="scss" scoped>
@import '@/css/breakpoints.css';

.floating-button {
  position: fixed;
  bottom: 2rem;
  right: 1.5rem;
  padding: 1.5rem 1rem;
  font-weight: 600;
  background: var(--light);
  color: var(--dark);
  border: none;
  border-radius: 4.25rem;
  font-size: var(--text-lg);
  cursor: pointer;
  transition: opacity 0.5s ease, transform 0.3s ease;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 1rem;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  opacity: 1;

  .arrow-icon {
    transform: rotate(90deg);
    width: 2rem;
    height: 2rem;

    :deep(path), :deep(line), :deep(polyline) {
      stroke: var(--dark);
      stroke-width: 0.3em;
    }
  }

  &:hover {
    transform: translateY(-2px);
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
}

@media (--phone) {
  .floating-button {
    display: none;
  }
}
</style>
